'use client';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { QUERY_KEYS } from '../../../../lib/constants';
import { useWishlistItems } from '../../../../wishlist/client';
import type { WishlistProducts } from '../server/get-wishlist-products';

export function useWishlistProducts() {
  const { data: allItems, isLoading: isItemsLoading } = useWishlistItems();

  // Extract product GIDs from ALL wishlist items (not just default list)
  const productGids = useMemo(() => {
    if (!allItems) return [];

    // Convert numeric product IDs to GIDs
    const gids = allItems.map(
      (item) => `gid://shopify/Product/${item.productId}`
    );

    // Remove duplicates
    return Array.from(new Set(gids));
  }, [allItems]);

  return useQuery({
    queryKey: [QUERY_KEYS.WISHLIST_PRODUCTS, productGids],
    queryFn: async () => await getWishlistProducts({ productGids }),
    enabled: !isItemsLoading && productGids.length > 0,
    placeholderData: (prevData) => prevData,
  });
}

export async function getWishlistProducts({
  productGids,
}: {
  productGids: string[];
}): Promise<WishlistProducts> {
  if (!productGids.length) {
    return [];
  }

  const searchParams = new URLSearchParams({
    productGids: JSON.stringify(productGids),
  });

  const response = await fetch(`/api/sanity/wishlist/products?${searchParams}`);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch wishlist products: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();
  return data;
}
